import { BoardTabs, type BoardTab } from "@/components/board/BoardTabs";
import { ActiveProjectsTab } from "@/components/projects/ActiveProjectsTab";
import { ArchivedProjectsTab } from "@/components/projects/ArchivedProjectsTab";
import { ClientsTab } from "@/components/clients/ClientsTab";
import { useQueryParams } from "@/hooks/useQueryParams";

type ProjectsTab = "current" | "archived" | "clients";

const PROJECT_TABS: BoardTab<ProjectsTab>[] = [
  { id: "current", label: "Current" },
  { id: "archived", label: "Archived" },
  { id: "clients", label: "Clients" },
];

function parseTab(value: string | null): ProjectsTab {
  if (value === "archived") return "archived";
  if (value === "clients") return "clients";
  return "current";
}

/**
 * Projects page - hosts the Current, Archived, and Clients tabs. The active tab
 * is driven by the `projectsTab` query param so it survives reloads and is
 * deep-linkable; the default (no param) is the Current board.
 */
export default function Projects() {
  const { get, setParam } = useQueryParams();
  // Page-specific param name so it doesn't collide with the Makers or Action
  // Items pages, which also use a tab param of their own.
  const tab: ProjectsTab = parseTab(get("projectsTab"));

  const handleTabChange = (next: ProjectsTab) => {
    // Keep the URL clean: the Current board is the default, so it carries no param.
    setParam("projectsTab", next === "current" ? null : next);
  };

  const tabs = (
    <BoardTabs
      tabs={PROJECT_TABS}
      active={tab}
      onChange={handleTabChange}
      ariaLabel="Projects sections"
    />
  );

  if (tab === "archived") return <ArchivedProjectsTab header={tabs} />;
  if (tab === "clients") return <ClientsTab header={tabs} />;
  return <ActiveProjectsTab header={tabs} />;
}
